import {CollisionEventType, Component, ForceMode, InputComponent, Object3D, PhysXComponent} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import Logger from './utils/logger.js';
import { vec3 } from 'gl-matrix';
import { vectorProjection } from './utils/utils.js';
import { MenuController } from './menus/menu-controller.js';
import { PlayerMovementHandler } from './player-movement-handler.js';

const MODULE = 'JoystickPhysxControls';

const _direction = new Float32Array(3);

/**
 * joystick-physx-motion
 */
export class JoystickPhysxMotion extends Component {
    static TypeName = 'joystick-physx-motion';
    
    @property.bool()
    activeInXR: boolean = false;

    @property.bool()
    activeOutXR: boolean = false;

    @property.bool()
    lockY: boolean = false;

    @property.object({ required: true })
    private controller?: Object3D;

    @property.object({ required: true })
    private jumpController?: Object3D;

    @property.int(5)
    private jumpButtonIndex: number = 5;

    @property.object()
    headObject: Object3D | null = null;

    @property.object()
    playerObject: Object3D | null = null; //must have a player-movement-handler component

    @property.float(1000.0)
    acceleration: number = 1000.0;

    @property.float(10.0)
    maxSpeed: number = 10.0;

    @property.float(0.1)
    private lowThreshold: number = 0.1;

    @property.int(6)
    platformMask: number = 6; // Default to group 6

    _platformMask: number = 1 << this.platformMask
    
    private inXR = false;
    private input?: InputComponent;
    private jumpInput?: InputComponent;
    private physxComp: PhysXComponent | null = null;
    private _playerMovementHandler: PlayerMovementHandler | null = null;
  
    start() {
        this.headObject = this.headObject || this.object;

        const tempInput = this.controller?.getComponent(InputComponent);
		if (!tempInput) {
			throw new Error(
				"The joystick-physx-motion component requires an input component on controller object",
			);
		}
        const tempPhysx = this.object.getComponent('physx') as PhysXComponent;
        if (!tempPhysx) {
            throw new Error(
                "The joystick-physx-motion component requires a physx component on the same object",
            );
        }
        const tempJumpInput = this.jumpController?.getComponent(InputComponent);
        if (!tempJumpInput) {
            throw new Error(
                "The joystick-physx-motion component requires an input component on jumpController object",
            );
        }

        const tempPlayerMove = this.playerObject?.getComponent(PlayerMovementHandler);
        if (!tempPlayerMove) {
            throw new Error(
                "The joystick-physx-motion component requires a player-movement-handler component on playerObject",
            );
        }

		this.input = tempInput;
        this.jumpInput = tempJumpInput;
        this.physxComp = tempPhysx;        
        this._playerMovementHandler = tempPlayerMove;

        
        this.engine.onXRSessionStart.add(this.onXRSessionStart.bind(this));
        this.engine.onXRSessionEnd.add(this.onXRSessionEnd.bind(this));
    }

    onXRSessionStart (session : XRSession) {
        this.inXR = true;
    }

    onXRSessionEnd () {
        this.inXR = false;
    }

    update(delta: number) {
        if ((this.inXR && this.activeInXR) || (!this.inXR && this.activeOutXR)) {
            if (!this.input?.xrInputSource?.gamepad?.axes?.length) {
                return;
            }

            const currentStrafeAxis = this.input.xrInputSource.gamepad.axes[2];
            const currentFaceAxis = this.input.xrInputSource.gamepad.axes[3];

            let accel = this.acceleration * delta;
            vec3.zero(_direction);

            if (this.jumpInput?.xrInputSource?.gamepad) {
                if (this.jumpInput.xrInputSource.gamepad.buttons[this.jumpButtonIndex].pressed) {
                    this._playerMovementHandler?.requestJump();
                }
            }
            if (delta <= 0 || Math.abs(currentStrafeAxis) < this.lowThreshold && Math.abs(currentFaceAxis) < this.lowThreshold) {
                return;
            }

            _direction[2] = currentFaceAxis;
            _direction[0] = currentStrafeAxis;
            vec3.normalize(_direction, _direction);

            _direction[0] *= accel;
            _direction[2] *= accel;
            this.headObject?.transformVectorWorld(_direction);
            if (this.lockY) {
                _direction[1] = 0;
                vec3.normalize(_direction, _direction);
                vec3.scale(_direction, _direction, accel);
            }

            // Limit speed. Should only apply to joystick movement, not other forces (like jump)
            // Only limit horizontal speed (x,z plane), allow unlimited vertical speed (y-axis)
            let currentSpeed = 0;
            if (this.physxComp) {
                // Measure speed only in x,z plane (horizontal)
                const horizontalVelocity = vec3.fromValues(
                    this.physxComp.linearVelocity[0],
                    0,
                    this.physxComp.linearVelocity[2]
                );
                currentSpeed = vec3.length(horizontalVelocity);
                if (Math.abs(currentSpeed) > this.maxSpeed) {
                    // Subtract only the horizontal component to preserve vertical movement
                    vec3.subtract(_direction, _direction, vectorProjection(_direction, horizontalVelocity));
                }
            }

            this._playerMovementHandler?.requestMovement(_direction);

        }
    }
}
